import React, { useState, useEffect, useCallback } from 'react';
import { 
  Book, Plus, Search, Edit, Trash2, RefreshCw, 
  AlertCircle, ChevronDown, ChevronRight, Copy, Check,
  Code, FileJson, Globe, Smartphone, Tv, Monitor,
  Lock, Unlock, Save, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

// Method colors
const METHOD_COLORS = {
  GET: 'bg-green-500/20 text-green-400 border-green-500/30',
  POST: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  PUT: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  PATCH: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  DELETE: 'bg-red-500/20 text-red-400 border-red-500/30',
};

// Categories
const CATEGORIES = [
  { value: 'authentication', label: 'Authentication', icon: Lock },
  { value: 'cameras', label: 'Cameras', icon: Monitor },
  { value: 'radio', label: 'Radio', icon: Globe },
  { value: 'sessions', label: 'Sessions', icon: Code },
  { value: 'ads', label: 'Ads', icon: Tv },
  { value: 'user', label: 'User & Devices', icon: Smartphone },
  { value: 'admin', label: 'Admin', icon: Lock },
];

// Code example tabs
const CODE_LANGUAGES = [
  { key: 'javascript', label: 'JavaScript', icon: '🌐' },
  { key: 'swift', label: 'Swift', icon: '🍎' },
  { key: 'kotlin', label: 'Kotlin', icon: '🤖' },
  { key: 'brightscript', label: 'BrightScript', icon: '📺' },
];

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="h-8 px-2">
      {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
};

// Code block component
const CodeBlock = ({ code, language }) => {
  if (!code) return <p className="text-muted-foreground text-sm italic">No example available</p>;
  
  return (
    <div className="relative">
      <div className="absolute right-2 top-2">
        <CopyButton text={code} />
      </div>
      <pre className="bg-black/50 rounded-lg p-4 overflow-x-auto text-sm">
        <code className="text-green-400 font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );
};

// JSON display component
const JsonBlock = ({ data, title }) => {
  const jsonStr = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  
  return (
    <div className="space-y-2">
      {title && <p className="text-sm font-medium text-white">{title}</p>}
      <div className="relative">
        <div className="absolute right-2 top-2">
          <CopyButton text={jsonStr} />
        </div>
        <pre className="bg-black/50 rounded-lg p-4 overflow-x-auto text-sm max-h-64">
          <code className="text-amber-400 font-mono whitespace-pre">{jsonStr}</code>
        </pre>
      </div>
    </div>
  );
};

// Endpoint card component
const EndpointCard = ({ doc, onEdit, onDelete, isExpanded, onToggle }) => {
  return (
    <Card className="border-border bg-card/50">
      <Collapsible open={isExpanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-secondary/30 transition-colors">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <Badge className={`${METHOD_COLORS[doc.method]} font-mono text-xs px-2 py-1`}>
              {doc.method}
            </Badge>
            <code className="font-mono text-sm text-white flex-1">{doc.endpoint}</code>
            <span className="text-sm text-muted-foreground hidden sm:block">{doc.title}</span>
            {doc.auth_required ? (
              <Lock className="h-4 w-4 text-amber-400" title="Auth Required" />
            ) : (
              <Unlock className="h-4 w-4 text-green-400" title="Public" />
            )}
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t border-border/50">
            <div className="space-y-6 pt-4">
              {/* Description */}
              <div>
                <h4 className="text-sm font-medium text-white mb-2">Description</h4>
                <p className="text-muted-foreground text-sm">{doc.description || 'No description'}</p>
              </div>
              
              {/* Platform Notes */}
              {doc.platform_notes && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-sm text-blue-400">
                    <strong>Platform Notes:</strong> {doc.platform_notes}
                  </p>
                </div>
              )}
              
              {/* Request */}
              {(doc.request_headers?.length > 0 || doc.request_body) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-white">Request</h4>
                  
                  {doc.request_headers?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Headers:</p>
                      <div className="bg-black/30 rounded p-2 space-y-1">
                        {doc.request_headers.map((h, i) => (
                          <div key={i} className="font-mono text-xs">
                            <span className="text-purple-400">{h.key}</span>
                            <span className="text-white">: </span>
                            <span className="text-green-400">{h.value}</span>
                            {h.required && <span className="text-red-400 ml-2">*</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {doc.request_body && (
                    <JsonBlock data={doc.request_body} title="Body:" />
                  )}
                </div>
              )}
              
              {/* Response */}
              {doc.response && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-white">Response</h4>
                  <JsonBlock data={doc.response} />
                </div>
              )}
              
              {/* Code Examples */}
              {doc.code_examples && Object.keys(doc.code_examples).some(k => doc.code_examples[k]) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-white">Code Examples</h4>
                  <Tabs defaultValue="javascript" className="w-full">
                    <TabsList className="bg-secondary/50">
                      {CODE_LANGUAGES.map(lang => (
                        <TabsTrigger key={lang.key} value={lang.key} className="text-xs">
                          {lang.icon} {lang.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {CODE_LANGUAGES.map(lang => (
                      <TabsContent key={lang.key} value={lang.key}>
                        <CodeBlock code={doc.code_examples?.[lang.key]} language={lang.key} />
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-border/50">
                <Button variant="outline" size="sm" onClick={() => onEdit(doc)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => onDelete(doc)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

// Edit Dialog Component
const EditDocDialog = ({ doc, open, onClose, onSave, isNew }) => {
  const [formData, setFormData] = useState({
    category: 'authentication',
    method: 'GET',
    endpoint: '',
    title: '',
    description: '',
    auth_required: true,
    request_headers: [],
    request_body: '',
    response: '',
    code_examples: { javascript: '', swift: '', kotlin: '', brightscript: '' },
    platform_notes: '',
    order: 0,
  });
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    if (doc) {
      setFormData({
        ...doc,
        request_body: typeof doc.request_body === 'object' ? JSON.stringify(doc.request_body, null, 2) : doc.request_body || '',
        response: typeof doc.response === 'object' ? JSON.stringify(doc.response, null, 2) : doc.response || '',
        code_examples: doc.code_examples || { javascript: '', swift: '', kotlin: '', brightscript: '' },
      });
    } else {
      setFormData({
        category: 'authentication',
        method: 'GET',
        endpoint: '',
        title: '',
        description: '',
        auth_required: true,
        request_headers: [],
        request_body: '',
        response: '',
        code_examples: { javascript: '', swift: '', kotlin: '', brightscript: '' },
        platform_notes: '',
        order: 0,
      });
    }
  }, [doc]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleCodeExampleChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      code_examples: { ...prev.code_examples, [lang]: value }
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Parse JSON fields
      const data = {
        ...formData,
        request_body: formData.request_body ? JSON.parse(formData.request_body) : null,
        response: formData.response ? JSON.parse(formData.response) : null,
      };
      
      await onSave(data);
      onClose();
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Invalid JSON in request body or response');
      } else {
        toast.error(err.message || 'Failed to save');
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add API Endpoint' : 'Edit API Endpoint'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Method</Label>
              <Select value={formData.method} onValueChange={(v) => handleChange('method', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Endpoint</Label>
              <Input 
                value={formData.endpoint} 
                onChange={(e) => handleChange('endpoint', e.target.value)}
                placeholder="/api/auth/login"
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Title</Label>
              <Input 
                value={formData.title} 
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="User Login"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea 
              value={formData.description} 
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Authenticate user and receive JWT token..."
              rows={2}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <Label className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={formData.auth_required}
                onChange={(e) => handleChange('auth_required', e.target.checked)}
                className="rounded"
              />
              Authentication Required
            </Label>
            
            <div className="flex items-center gap-2">
              <Label>Order:</Label>
              <Input 
                type="number" 
                value={formData.order} 
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                className="w-20"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Platform Notes</Label>
            <Input 
              value={formData.platform_notes} 
              onChange={(e) => handleChange('platform_notes', e.target.value)}
              placeholder="Roku devices should use roku_hls field..."
            />
          </div>
          
          {/* Request/Response JSON */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Request Body (JSON)</Label>
              <Textarea 
                value={formData.request_body} 
                onChange={(e) => handleChange('request_body', e.target.value)}
                placeholder='{"username": "string", "password": "string"}'
                className="font-mono text-sm"
                rows={6}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Response (JSON)</Label>
              <Textarea 
                value={formData.response} 
                onChange={(e) => handleChange('response', e.target.value)}
                placeholder='{"access_token": "jwt...", "user": {...}}'
                className="font-mono text-sm"
                rows={6}
              />
            </div>
          </div>
          
          {/* Code Examples */}
          <div className="space-y-3">
            <Label>Code Examples</Label>
            <Tabs defaultValue="javascript">
              <TabsList className="bg-secondary/50">
                {CODE_LANGUAGES.map(lang => (
                  <TabsTrigger key={lang.key} value={lang.key} className="text-xs">
                    {lang.icon} {lang.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {CODE_LANGUAGES.map(lang => (
                <TabsContent key={lang.key} value={lang.key}>
                  <Textarea 
                    value={formData.code_examples?.[lang.key] || ''} 
                    onChange={(e) => handleCodeExampleChange(lang.key, e.target.value)}
                    placeholder={`// ${lang.label} example code...`}
                    className="font-mono text-sm"
                    rows={8}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Endpoint'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export function ApiDocs() {
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedDocs, setExpandedDocs] = useState(new Set());
  
  // Edit state
  const [editDoc, setEditDoc] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isNewDoc, setIsNewDoc] = useState(false);
  
  // Delete state
  const [deleteDoc, setDeleteDoc] = useState(null);

  const fetchDocs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getApiDocs();
      setDocs(data.docs || data || []);
    } catch (err) {
      setError(err.message || 'Failed to load API documentation');
      // Use empty array if API not ready
      setDocs([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleCreate = () => {
    setEditDoc(null);
    setIsNewDoc(true);
    setShowEditDialog(true);
  };

  const handleEdit = (doc) => {
    setEditDoc(doc);
    setIsNewDoc(false);
    setShowEditDialog(true);
  };

  const handleSave = async (data) => {
    if (isNewDoc) {
      await api.createApiDoc(data);
      toast.success('Endpoint documentation created');
    } else {
      await api.updateApiDoc(editDoc._id, data);
      toast.success('Endpoint documentation updated');
    }
    fetchDocs();
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    try {
      await api.deleteApiDoc(deleteDoc._id);
      toast.success('Endpoint documentation deleted');
      setDeleteDoc(null);
      fetchDocs();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const toggleExpanded = (docId) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  // Filter docs
  const filteredDocs = docs.filter(doc => {
    const matchesSearch = !searchQuery || 
      doc.endpoint?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedDocs = CATEGORIES.reduce((acc, cat) => {
    const categoryDocs = filteredDocs
      .filter(d => d.category === cat.value)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    if (categoryDocs.length > 0) {
      acc[cat.value] = categoryDocs;
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="api-docs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">API Documentation</h1>
          <p className="text-muted-foreground">Reference guide for RailStream API endpoints</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchDocs} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Endpoint
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border bg-card">
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search endpoints..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="flex items-center gap-4 p-4">
            <AlertCircle className="h-6 w-6 text-yellow-400" />
            <div className="flex-1">
              <p className="font-medium text-yellow-400">API Documentation endpoint not available</p>
              <p className="text-sm text-muted-foreground">Click "Add Endpoint" to start building your documentation. The backend endpoint needs to be created.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documentation */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : Object.keys(groupedDocs).length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Book className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium text-white">No documentation yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Start building your API documentation
            </p>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Endpoint
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {CATEGORIES.map(cat => {
            const categoryDocs = groupedDocs[cat.value];
            if (!categoryDocs) return null;
            
            const Icon = cat.icon;
            
            return (
              <div key={cat.value} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold text-white">{cat.label}</h2>
                  <Badge variant="secondary">{categoryDocs.length}</Badge>
                </div>
                <div className="space-y-2">
                  {categoryDocs.map(doc => (
                    <EndpointCard
                      key={doc._id}
                      doc={doc}
                      onEdit={handleEdit}
                      onDelete={setDeleteDoc}
                      isExpanded={expandedDocs.has(doc._id)}
                      onToggle={() => toggleExpanded(doc._id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <EditDocDialog
        doc={editDoc}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSave={handleSave}
        isNew={isNewDoc}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Documentation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the documentation for{' '}
              <code className="text-primary">{deleteDoc?.endpoint}</code>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ApiDocs;
